import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCart } from '@/contexts/CartContext';
import { createStoreOrder } from '@/lib/api';
import { 
  ShoppingCart, Trash2, Plus, Minus, Loader2, 
  Package, Send, X, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const CartDrawer = ({ storeId, storeName, isUSA = false }) => {
  const { cart, isOpen, setIsOpen, updateItem, removeItem, loading, emptyCart } = useCart();
  const [showCheckout, setShowCheckout] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_company: '',
    shipping_address: '',
    shipping_city: '',
    shipping_state: '',
    notes: ''
  });

  const handleQuantityChange = async (item, delta) => {
    const newQuantity = item.quantity + delta;
    if (newQuantity <= 0) {
      await removeItem(item.product_id, item.variant_id, item.variant_options);
    } else {
      await updateItem({
        product_id: item.product_id,
        product_name: item.product_name,
        variant_id: item.variant_id,
        variant_name: item.variant_name,
        variant_options: item.variant_options,
        quantity: newQuantity,
        unit: item.unit,
        unit_price: item.unit_price
      });
    }
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const orderData = {
        store_id: storeId,
        items: cart.items,
        ...customerInfo
      };

      await createStoreOrder(storeId, orderData);
      setOrderSuccess(true);
      toast.success(isUSA ? 'Order sent successfully!' : '¡Pedido enviado correctamente!');
    } catch (error) {
      toast.error(error.message || (isUSA ? 'Error sending order' : 'Error al enviar pedido'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setShowCheckout(false);
    if (orderSuccess) {
      setOrderSuccess(false);
      emptyCart();
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            {isUSA ? 'Shopping Cart' : 'Carrito de Compras'}
          </SheetTitle>
        </SheetHeader>

        {orderSuccess ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-6">
            <CheckCircle className="w-16 h-16 text-emerald-500 mb-4" />
            <h3 className="text-xl font-semibold mb-2">
              {isUSA ? 'Order sent!' : '¡Pedido enviado!'}
            </h3>
            <p className="text-slate-500 mb-6">
              {isUSA 
                ? 'The seller will contact you soon to finalize the order.'
                : 'El vendedor se pondrá en contacto contigo pronto para finalizar el pedido.'}
            </p>
            <Button onClick={handleClose} className="bg-emerald-600 hover:bg-emerald-700">
              {isUSA ? 'Continue shopping' : 'Seguir comprando'}
            </Button>
          </div>
        ) : showCheckout ? (
          <form onSubmit={handleSubmitOrder} className="flex-1 overflow-y-auto space-y-4 py-4">
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900">
                {isUSA ? 'Contact Information' : 'Información de Contacto'}
              </h3>
              
              <div className="space-y-2">
                <Label htmlFor="customer_name">{isUSA ? 'Full Name *' : 'Nombre Completo *'}</Label>
                <Input
                  id="customer_name"
                  value={customerInfo.customer_name}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, customer_name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customer_email">{isUSA ? 'Email *' : 'Correo Electrónico *'}</Label>
                <Input
                  id="customer_email"
                  type="email"
                  value={customerInfo.customer_email}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, customer_email: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customer_phone">{isUSA ? 'Phone' : 'Teléfono'}</Label>
                <Input
                  id="customer_phone"
                  value={customerInfo.customer_phone}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, customer_phone: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customer_company">{isUSA ? 'Company (optional)' : 'Empresa (opcional)'}</Label>
                <Input
                  id="customer_company"
                  value={customerInfo.customer_company}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, customer_company: e.target.value })}
                />
              </div>

              <h3 className="font-semibold text-slate-900 pt-2">
                {isUSA ? 'Shipping Address' : 'Dirección de Envío'}
              </h3>

              <div className="space-y-2">
                <Label htmlFor="shipping_address">{isUSA ? 'Address *' : 'Dirección *'}</Label>
                <Textarea
                  id="shipping_address"
                  value={customerInfo.shipping_address}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, shipping_address: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="shipping_city">{isUSA ? 'City *' : 'Ciudad *'}</Label>
                  <Input
                    id="shipping_city"
                    value={customerInfo.shipping_city}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, shipping_city: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shipping_state">{isUSA ? 'State *' : 'Estado *'}</Label>
                  <Input
                    id="shipping_state"
                    value={customerInfo.shipping_state}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, shipping_state: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">{isUSA ? 'Notes (optional)' : 'Notas (opcional)'}</Label>
                <Textarea
                  id="notes"
                  value={customerInfo.notes}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, notes: e.target.value })}
                  placeholder={isUSA ? 'Special instructions...' : 'Instrucciones especiales...'}
                />
              </div>
            </div>

            <div className="pt-4 border-t space-y-3">
              <div className="flex justify-between text-lg font-semibold">
                <span>Total:</span>
                <span className="text-emerald-600">
                  {cart.subtotal ? `$${cart.subtotal.toLocaleString()} MXN` : (isUSA ? 'Quote pending' : 'Por cotizar')}
                </span>
              </div>
              <Button 
                type="submit" 
                disabled={submitting}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {isUSA ? 'Send Order' : 'Enviar Pedido'}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                className="w-full"
                onClick={() => setShowCheckout(false)}
              >
                {isUSA ? 'Back to cart' : 'Volver al carrito'}
              </Button>
            </div>
          </form>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto py-4">
              {cart.items?.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <Package className="w-16 h-16 text-slate-300 mb-4" />
                  <p className="text-slate-500">
                    {isUSA ? 'Your cart is empty' : 'Tu carrito está vacío'}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {cart.items?.map((item, index) => (
                    <div key={`${item.product_id}-${item.variant_id || index}`} className="flex gap-4 p-3 bg-slate-50 rounded-lg">
                      {item.image_url && (
                        <img 
                          src={item.image_url} 
                          alt={item.product_name}
                          className="w-20 h-20 object-cover rounded"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm truncate">{item.product_name}</h4>
                        {item.variant_name && (
                          <p className="text-xs text-slate-500">{item.variant_name}</p>
                        )}
                        <p className="text-xs text-slate-500">{item.unit}</p>
                        {item.unit_price && (
                          <p className="text-sm font-semibold text-emerald-600">
                            ${item.unit_price.toLocaleString()} MXN
                          </p>
                        )}
                        
                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-7 w-7"
                            onClick={() => handleQuantityChange(item, -1)}
                            disabled={loading}
                          >
                            <Minus className="w-3 h-3" />
                          </Button>
                          <span className="w-8 text-center text-sm font-medium">
                            {item.quantity}
                          </span>
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-7 w-7"
                            onClick={() => handleQuantityChange(item, 1)}
                            disabled={loading}
                          >
                            <Plus className="w-3 h-3" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-red-500 ml-auto"
                            onClick={() => removeItem(item.product_id, item.variant_id, item.variant_options)}
                            disabled={loading}
                            data-testid={`remove-item-${item.product_id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {cart.items?.length > 0 && (
              <SheetFooter className="border-t pt-4 flex-col gap-3">
                <div className="flex justify-between w-full text-lg font-semibold">
                  <span>Subtotal:</span>
                  <span className="text-emerald-600">
                    {cart.subtotal ? `$${cart.subtotal.toLocaleString()} MXN` : (isUSA ? 'Quote pending' : 'Por cotizar')}
                  </span>
                </div>
                <Button 
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => setShowCheckout(true)}
                >
                  {isUSA ? 'Continue to checkout' : 'Continuar al checkout'}
                </Button>
              </SheetFooter>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
